import { AsyncPipe, NgOptimizedImage, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, ViewEncapsulation } from '@angular/core';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FilePicker } from '@capawesome/capacitor-file-picker';
import { Platform } from '@ionic/angular';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DswButtonModule, DswFormFieldModule, DswToastService } from '@vi-succ/design-system-web';
import { BehaviorSubject, catchError } from 'rxjs';
import { EtFeedbackService } from '../../services/et-feedback.service';
import { FileSizePipe } from '../../util/filesize.pipe';

export interface FeedbackDialogData {
  email: string;
}

export interface Feedback {
  email: string;
  positive: string;
  negative: string;
  files: FileUpload[];
  additionalData: {
    [key: string]: string;
  };
}

export interface FileUpload {
  name: string;
  blob: Blob | undefined;
  mimeType: string;
  size: number;
}

@Component({
  selector: 'et-feedback',
  templateUrl: './feedback-dialog.component.html',
  styleUrls: ['./feedback-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    DswButtonModule,
    NgOptimizedImage,
    MatProgressSpinnerModule,
    AsyncPipe,
    TranslateModule,
    NgTemplateOutlet,
    DswFormFieldModule,
    FileSizePipe,
  ],
})
export class FeedbackDialogComponent {
  formGroup = new FormGroup({
    email: new FormControl('', [Validators.email, Validators.required]),
    positive: new FormControl('', [Validators.required]),
    negative: new FormControl('', [Validators.required]),
    additionalData: new FormControl({}),
    files: new FormArray<any>([]),
  });
  feedbackSent = false;
  isLoading$ = new BehaviorSubject<boolean>(false);
  isDesktop = this.platform.is('desktop');

  protected readonly onsubmit = onsubmit;

  constructor(
    private feedbackService: EtFeedbackService,
    private changeDetectorRef: ChangeDetectorRef,
    private toastService: DswToastService,
    private translate: TranslateService,
    private platform: Platform,
    @Inject(MAT_DIALOG_DATA) data: FeedbackDialogData
  ) {
    if (data.email) {
      this.formGroup.get('email')?.patchValue(data.email);
    }
  }

  get files(): FileUpload[] {
    if (this.formGroup && this.formGroup.value) {
      const feedback = this.formGroup.value as Feedback;
      return feedback.files;
    } else {
      return [];
    }
  }

  async openFilePicker() {
    const images = await FilePicker.pickImages({ readData: true });
    if (images) {
      const attachments = images.files.map((image) => {
        let blob = image.blob;

        if (!blob && image.data) {
          blob = this.base64ToBlob(image.data, image.mimeType);
        }

        const file: FileUpload = {
          name: image.name,
          blob: blob,
          mimeType: image.mimeType,
          size: image.size,
        };
        return file;
      });

      this.addFiles(attachments);
    }
  }

  addFiles(files: FileUpload[]) {
    const filesArray = this.formGroup.controls.files;
    files.forEach((file) => {
      filesArray.push(new FormControl(file));
    });
    this.changeDetectorRef.detectChanges();
  }

  onSubmitFeedback() {
    if (this.formGroup.invalid) {
      return;
    }
    this.isLoading$.next(true);
    this.feedbackService
      .sendFeedback(this.formGroup.value as Feedback)
      .pipe(
        catchError((err) => {
          this.isLoading$.next(false);
          this.toastService.open(this.translate.instant('feedback.error'), 'error');
          throw err;
        })
      )
      .subscribe(() => {
        this.isLoading$.next(false);
        this.feedbackSent = true;
        this.changeDetectorRef.detectChanges();
      });
  }

  removeFile(index: number) {
    this.formGroup.controls.files.removeAt(index);
  }

  private base64ToBlob(base64: string, mimeType: string): Blob {
    // Decode base64 string
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);

    // Convert decoded string to bytes
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);

    // Create blob from bytes
    const blob = new Blob([byteArray], { type: mimeType });

    return blob;
  }
}
